<template>
  <div
    v-if="impersonation.active && principal"
    class="flex h-9 items-center justify-between gap-3 border-b border-warning-700 bg-warning-100 px-4 font-geist text-xsm text-typography-primary"
    data-testid="impersonation-banner"
  >
    <div class="flex min-w-0 items-center gap-2">
      <LineEye class="h-4 w-4 shrink-0 fill-path-current text-warning-700" />
      <span class="truncate">
        Viewing as <strong>{{ principal.displayName }}</strong>
        <span class="text-typography-secondary">({{ principal.email }})</span>
        · {{ impersonation.session?.account.name }} · {{ principal.effectiveRole }} — <strong>read only</strong>
      </span>
    </div>

    <div class="flex shrink-0 items-center gap-3">
      <span class="text-typography-secondary" data-testid="impersonation-remaining">{{ remaining }}</span>
      <PulseButton variant="secondary" size="x-small" data-testid="impersonation-exit" @click="impersonation.stop()">
        Exit
      </PulseButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useImpersonationStore } from '~/store/impersonation';

const impersonation = useImpersonationStore();

const remaining = ref('');
let timer: ReturnType<typeof setInterval> | undefined;

const principal = computed(() => impersonation.session?.principal);

function tick() {
  const expiresAt = impersonation.session?.expiresAt;
  if (!expiresAt) return;

  const msLeft = new Date(expiresAt).getTime() - Date.now();

  if (msLeft <= 0) {
    remaining.value = 'expired';
    // The server already treats the session as terminal; stop() clears the cookie and reloads so the engineer is
    // never left looking at a stale target view.
    impersonation.stop();

    return;
  }

  const minutes = Math.floor(msLeft / 60_000);
  const seconds = Math.floor((msLeft % 60_000) / 1000);
  remaining.value = `${minutes}:${String(seconds).padStart(2, '0')}`;
}

onMounted(() => {
  tick();
  timer = setInterval(tick, 1000);
});

onUnmounted(() => {
  if (timer) clearInterval(timer);
});
</script>
